import React from 'react';
import { Plus, AlertTriangle } from 'lucide-react';
import { WarehouseCard } from '../components/WarehouseCard';
import { MAX_WAREHOUSES } from '../../../../../../features/sellers/sellerFormConstants';

/**
 * The seller's warehouses. At least one is required — the backend refuses an
 * empty list — and the first one becomes the company's default and return
 * warehouse, which is why the cards say so and why their order matters.
 *
 * @param {object} props
 * @param {Array<object>} props.warehouses
 * @param {Array<object>} [props.errors] - Sparse, indexed like `warehouses`
 * @param {string} props.country
 * @param {Array<{value: number, label: string}>} props.zoneOptions
 * @param {boolean} props.loadingZones
 * @param {(id: string, patch: object) => void} props.onWarehouseChange
 * @param {() => void} props.onAddWarehouse
 * @param {(id: string) => void} props.onRemoveWarehouse
 */
export const WarehousesStep = ({
  warehouses,
  errors = [],
  country,
  zoneOptions,
  loadingZones,
  onWarehouseChange,
  onAddWarehouse,
  onRemoveWarehouse,
}) => {
  const canAdd = warehouses.length < MAX_WAREHOUSES;
  const hasZones = zoneOptions.length > 0;

  return (
    <div className="space-y-5">
      {!loadingZones && !hasZones && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-300">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <p className="text-sm">
            Tu cuenta no tiene zonas de despacho creadas. Cada bodega necesita una, así que primero
            crea al menos una zona en <span className="font-semibold">Zonas</span> y vuelve a
            intentarlo.
          </p>
        </div>
      )}

      {warehouses.map((warehouse, index) => (
        <WarehouseCard
          key={warehouse.id}
          warehouse={warehouse}
          index={index}
          errors={errors[index] || {}}
          country={country}
          zoneOptions={zoneOptions}
          loadingZones={loadingZones}
          onChange={(patch) => onWarehouseChange(warehouse.id, patch)}
          onRemove={() => onRemoveWarehouse(warehouse.id)}
          canRemove={warehouses.length > 1}
        />
      ))}

      <div className="flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={onAddWarehouse}
          disabled={!canAdd}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-border text-text-secondary hover:text-ninja-green hover:border-ninja-green font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-text-secondary disabled:hover:border-border"
        >
          <Plus size={16} />
          Agregar bodega
        </button>

        <span className="text-xs text-text-muted">
          {warehouses.length} de {MAX_WAREHOUSES}
        </span>
      </div>
    </div>
  );
};
